import { create } from 'zustand'
import type { AuthUser, TotpChallenge } from '@/types/api'
import { api, clearRefreshToken, clearTokens, setTokens } from '@/lib/api-client'
import { disconnectSocket, reconnectSocket } from '@/lib/socket'
import { disablePush } from '@/lib/web-push'
import { setAppBadgeCount } from '@/lib/app-badge'
import { queryClient } from '@/lib/query-client'
import { withTurnstileToken } from '@/lib/turnstile'

interface AuthState {
  user: AuthUser | null
  loading: boolean
  /**
   * `remember` ("Duy trì đăng nhập", mặc định bật): false → token chỉ sống trong phiên trình duyệt (sessionStorage).
   * `termsAccepted`: BẮT BUỘC true để server cho đăng nhập — mỗi lần đăng nhập đều ghi 1 bằng chứng đồng ý Điều
   * khoản/Chính sách mới (NĐ 356/2025/NĐ-CP Điều 6), không chỉ một lần duy nhất lúc đăng ký.
   * Trả `TotpChallenge` (không phải AuthUser) khi mật khẩu đúng nhưng còn thiếu bước xác thực 2 lớp — xem
   * `pages/auth/totp-login-step.tsx` (dùng `completeTotpLogin`/`startTotpLoginSetup`/`confirmTotpLoginSetup`).
   */
  login: (email: string, password: string, remember?: boolean, turnstileToken?: string | null, termsAccepted?: boolean) => Promise<AuthUser | TotpChallenge>
  /** Nhận phiên từ đăng ký / Google (server trả token + refreshToken + user). */
  applySession: (data: { token: string; refreshToken?: string; user: AuthUser }, remember?: boolean) => AuthUser
  logout: () => Promise<void>
  loadMe: () => Promise<void>
  setUser: (u: AuthUser | null) => void
  /** Owner/admin xem hệ thống dưới quyền một nhân viên. */
  impersonate: (userId: string) => Promise<AuthUser>
  /** Thoát chế độ xem hộ, quay lại tài khoản quản trị gốc. */
  stopImpersonation: () => Promise<AuthUser>
  /** Hoàn tất đăng nhập khi tài khoản ĐÃ bật TOTP: nhập mã 6 số hoặc 1 mã dự phòng. */
  completeTotpLogin: (ticket: string, code: string, remember?: boolean) => Promise<AuthUser>
  /** Bắt buộc thiết lập (owner chưa bật TOTP) — bước 1: sinh secret + QR. */
  startTotpLoginSetup: (ticket: string) => Promise<{ otpauthUri: string; qrDataUrl: string }>
  /** Bắt buộc thiết lập — bước 2: xác nhận mã đầu tiên → bật + đăng nhập + trả 10 mã dự phòng (hiện 1 lần). */
  confirmTotpLoginSetup: (ticket: string, code: string, remember?: boolean) => Promise<{ user: AuthUser; backupCodes: string[] }>
}

/**
 * Danh tính (id người dùng) đang / vừa dùng trong lần mở trang này — để biết khi đăng nhập / tải hồ sơ có phải NGƯỜI KHÁC không.
 *
 * Vì sao phải xoá bộ nhớ đệm truy vấn khi đổi danh tính: khoá truy vấn KHÔNG gắn với người dùng (['conversations', …], ['dashboard-kpi']…)
 * và `staleTime` là 30 giây, nên đăng xuất → tài khoản khác đăng nhập trong lúc trang vẫn mở sẽ dùng lại dữ liệu của người trước
 * (hội thoại, số đếm, danh sách kênh…) tới khi tải lại trang (lỗi báo 05/10/2026). Đây còn là rò rỉ dữ liệu giữa tổ chức trên giao diện.
 */
let lastIdentity: string | null = null

/** Bỏ MỌI dữ liệu đã nạp của danh tính cũ: huỷ yêu cầu đang bay (khỏi ghi đè lại khi về muộn) rồi xoá sạch. */
export function dropIdentityData(): void {
  void queryClient.cancelQueries()
  queryClient.clear()
}

/**
 * Vào một danh tính. `force` = luôn xoá (xem hộ / thoát xem hộ: cùng trình duyệt nhưng phạm vi quyền khác hẳn);
 * không thì chỉ xoá khi là người KHÁC lần trước (cùng người đăng nhập lại — vd hết phiên — vẫn giữ trang đang làm dở).
 */
function enterIdentity(id: string, force = false): void {
  if (force || (lastIdentity !== null && lastIdentity !== id)) dropIdentityData()
  lastIdentity = id
}

/** Chỉ cho test: quên danh tính đã nhớ. */
export function __resetIdentityForTest(): void { lastIdentity = null }

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,

  login: async (email, password, remember = true, turnstileToken, termsAccepted = false) => {
    const { data } = await api.post('/auth/login', withTurnstileToken({ email, password, termsAccepted }, turnstileToken))
    // Xác thực 2 lớp (TOTP): mật khẩu đúng nhưng chưa xong — KHÔNG có token, chỉ có vé tạm (10 phút).
    if (data.totpRequired) return { totpRequired: true, mode: data.mode, ticket: data.ticket } as TotpChallenge
    setTokens(data.token, data.refreshToken, remember)
    enterIdentity(data.user.id)
    reconnectSocket()
    set({ user: data.user, loading: false })
    return data.user
  },

  applySession: (data, remember = true) => {
    setTokens(data.token, data.refreshToken, remember)
    enterIdentity(data.user.id)
    reconnectSocket()
    set({ user: data.user, loading: false })
    return data.user
  },

  logout: async () => {
    // Máy dùng chung: bỏ đăng ký thông báo đẩy của thiết bị này TRƯỚC khi mất token (cần đăng nhập để huỷ), kẹt quá 2 giây thì thôi.
    await Promise.race([disablePush(), new Promise<void>((r) => setTimeout(r, 2000))])
    try {
      await api.post('/auth/logout')
    } catch {
      /* bỏ qua lỗi mạng khi logout */
    }
    clearTokens()
    disconnectSocket()
    // Xoá số chưa đọc trên biểu tượng ứng dụng: thành phần đặt huy hiệu (NotificationCenter) bị huỷ ngay khi rời khung ứng dụng nên không tự xoá được.
    void setAppBadgeCount(0)
    // Người dùng kế tiếp (cùng trình duyệt, trang vẫn mở) không được thấy dữ liệu đã nạp của người này.
    dropIdentityData()
    lastIdentity = null
    set({ user: null })
  },

  loadMe: async () => {
    try {
      const { data } = await api.get('/auth/me')
      enterIdentity(data.id)
      set({ user: data, loading: false })
    } catch {
      set({ user: null, loading: false })
    }
  },

  setUser: (u) => set({ user: u }),

  impersonate: async (userId) => {
    // Backend chỉ trả access token (hạn 4h), không kèm refresh token. Phải xoá
    // refresh token của quản trị viên, nếu không 401 sẽ lặng lẽ cấp lại quyền
    // admin trong khi giao diện vẫn tưởng đang là nhân viên. Hết 4h → về /login.
    const { data } = await api.post(`/auth/impersonate/${userId}`)
    setTokens(data.token)
    clearRefreshToken()
    enterIdentity(data.user.id, true)
    reconnectSocket()
    set({ user: { ...data.user, impersonatedBy: data.user.impersonatedBy ?? null } })
    return data.user
  },

  stopImpersonation: async () => {
    const { data } = await api.post('/auth/stop-impersonation')
    setTokens(data.token, data.refreshToken)
    enterIdentity(data.user.id, true)
    reconnectSocket()
    set({ user: data.user })
    return data.user
  },

  completeTotpLogin: async (ticket, code, remember = true) => {
    const { data } = await api.post('/auth/totp/login-verify', { ticket, code })
    setTokens(data.token, data.refreshToken, remember)
    enterIdentity(data.user.id)
    reconnectSocket()
    set({ user: data.user, loading: false })
    return data.user
  },

  startTotpLoginSetup: async (ticket) => {
    const { data } = await api.post('/auth/totp/login-setup/start', { ticket })
    return data
  },

  confirmTotpLoginSetup: async (ticket, code, remember = true) => {
    const { data } = await api.post('/auth/totp/login-setup/confirm', { ticket, code })
    setTokens(data.token, data.refreshToken, remember)
    enterIdentity(data.user.id)
    reconnectSocket()
    set({ user: data.user, loading: false })
    return { user: data.user, backupCodes: data.backupCodes }
  },
}))
